import { useRequiredContext } from "@redotech/react-util/context";
import { useDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import BracketEllipsesIcon from "@redotech/redo-icons/brackets-ellipses_filled.svg";
import PenIcon from "@redotech/redo-icons/edit-03_filled.svg";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { useEnabledIntegrations } from "@redotech/redo-merchant-marketing-app/automations/use-enabled-integrations";
import {
  SendWebhookStep,
  SendWebhookStepAuthType,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { getVisibleDynamicVariables } from "@redotech/redo-model/advanced-flow/schemas/filter-schema-fields";
import {
  applyDynamicVariableSyntaxLiquid,
  DynamicVariable,
  SchemaType,
} from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { SchemaField } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { NonSchemaReferenceVariable } from "@redotech/redo-model/marketing/common/non-schema-variables";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { DynamicVariableSelectionModal } from "@redotech/redo-web/dynamic-variable-selection-modal";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "@redotech/redo-web/labeled-input";
import { Text } from "@redotech/redo-web/text";
import { truncateString } from "@redotech/util/string";
import { memo, useMemo, useState } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";

const isValidWebhookUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }

  // Remove Liquid template variables for validation
  // This allows URLs like https://example.com/{{ external_order_id }}
  const urlWithoutVariables = url.replace(/\{\{[^}]+\}\}/g, "placeholder");

  try {
    const parsedUrl = new URL(urlWithoutVariables);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const isValidHeaderName = (name: string): boolean => {
  if (!name) {
    return false;
  }
  // HTTP header names should only contain alphanumeric characters, hyphens, and underscores
  // Based on RFC 7230 but more restrictive for safety
  return /^[a-zA-Z0-9-_]+$/.test(name);
};

const PayloadEditorModal = memo(function PayloadEditorModal({
  isOpen,
  onClose,
  payload,
  setPayload,
  schemaType,
  availableDynamicVariables,
}: {
  isOpen: boolean;
  onClose: () => void;
  payload: string;
  setPayload: (payload: string) => void;
  schemaType: SchemaType | undefined;
  availableDynamicVariables: SchemaField[];
}) {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const [localPayload, setLocalPayload] = useState(payload);
  const [dynamicVariableSelectorOpen, setDynamicVariableSelectorOpen] =
    useState(false);
  const [payloadCursorPosition, setPayloadCursorPosition] = useState<number>(0);

  const debouncedPayload = useDebounce(localPayload, 1000);

  const previewResult = useLoad(async () => {
    if (!schemaType || !debouncedPayload) {
      return null;
    }

    return await marketingRpcClient.examplePopulatedLiquidTemplate({
      schemaType,
      template: debouncedPayload,
    });
  }, [schemaType, debouncedPayload, marketingRpcClient]);

  const handleVariableSelect = useHandler((variable: DynamicVariable) => {
    const liquidVariable = applyDynamicVariableSyntaxLiquid({
      key: variable.key,
      default: variable.type === "text" ? variable.default : undefined,
      isHtml: false,
      schemaType: schemaType as SchemaType | null,
    });
    const before = localPayload.substring(0, payloadCursorPosition);
    const after = localPayload.substring(payloadCursorPosition);
    const newPayload = `${before}${liquidVariable}${after}`;
    setLocalPayload(newPayload);
    setDynamicVariableSelectorOpen(false);
  });

  return (
    <>
      <RedoModal
        isOpen={isOpen}
        modalSize="xl"
        noPadding
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Save",
          onClick: () => {
            setPayload(localPayload);
            onClose();
          },
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose }}
        subtitle={
          <Flex align="center" dir="row" gap="xs">
            <Text fontSize="sm" textColor="tertiary">
              Use Liquid template syntax to insert dynamic data.
            </Text>
            <a
              href="https://liquidjs.com/tutorials/intro-to-liquid.html"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center" }}
              target="_blank"
            >
              <Text fontSize="sm" textColor="secondary">
                View Liquid docs
              </Text>
              <ExternalLinkIcon
                height="14"
                style={{ marginLeft: "4px" }}
                width="14"
              />
            </a>
          </Flex>
        }
        title="Edit webhook payload"
      >
        <Flex dir="row" gap="none" style={{ height: "500px" }}>
          <Flex
            dir="column"
            gap="lg"
            p="lg"
            style={{ flex: 2, display: "flex" }}
          >
            <Flex align="center" gap="sm" justify="space-between" shrink={0}>
              <Text fontSize="xs" fontWeight="medium">
                Payload (JSON)
              </Text>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={BracketEllipsesIcon}
                onClick={() => setDynamicVariableSelectorOpen(true)}
                size="xs"
                text="Insert variable"
              />
            </Flex>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <RedoTextareaInputField
                monospace
                onCursorPositionChange={(position) =>
                  setPayloadCursorPosition(position)
                }
                placeholder="{}"
                setValue={setLocalPayload}
                spellCheck={false}
                style={{ height: "100%" }}
                value={localPayload}
              />
            </div>
          </Flex>

          <Flex
            bgColor="tertiary"
            borderColor="secondary"
            borderLeftWidth="1px"
            borderStyle="solid"
            dir="column"
            gap="xs"
            p="lg"
            style={{ flex: 1, overflow: "hidden" }}
          >
            <Text fontSize="xs" fontWeight="medium">
              Preview
            </Text>
            <Flex
              bgColor="primary"
              borderColor={
                previewResult.error || previewResult.value?.success === false
                  ? "error"
                  : "secondary"
              }
              borderStyle="solid"
              borderWidth="1px"
              p="md"
              style={{ flex: 1, overflowX: "auto", overflowY: "auto" }}
            >
              <Text
                color={
                  previewResult.error || previewResult.value?.success === false
                    ? "error"
                    : undefined
                }
                fontFamily="monospace"
                fontSize="xs"
                style={{ whiteSpace: "pre" }}
              >
                {previewResult.pending
                  ? "Loading preview..."
                  : previewResult.error
                    ? previewResult.error.message
                    : previewResult.value?.success === false
                      ? previewResult.value.error
                      : previewResult.value?.template ||
                        "Preview will appear here..."}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </RedoModal>
      <DynamicVariableSelectionModal
        dynamicVariables={availableDynamicVariables}
        handleDynamicVariableSelected={handleVariableSelect}
        onClose={() => setDynamicVariableSelectorOpen(false)}
        open={dynamicVariableSelectorOpen}
      />
    </>
  );
});

const WebhookStepInspector = memo(function WebhookStepInspector(
  props: StepTypeDetailsProps<PartialWithRequiredType<SendWebhookStep>>,
) {
  const { step, setStep, onClose, showValidationErrors, schemaType, schema } =
    props;

  const [payloadEditorOpen, setPayloadEditorOpen] = useState(false);

  const enabledIntegrations = useEnabledIntegrations();

  const availableDynamicVariables: SchemaField[] = useMemo(() => {
    if (!schema) {
      return Object.values(NonSchemaReferenceVariable);
    }
    return getVisibleDynamicVariables(schema, enabledIntegrations);
  }, [schema, enabledIntegrations]);

  const authTypeOptions = useMemo(
    () => [
      {
        id: SendWebhookStepAuthType.NONE,
        text: "None",
        type: "text" as const,
        value: SendWebhookStepAuthType.NONE,
      },
      {
        id: SendWebhookStepAuthType.OAUTH2_CLIENT_CREDENTIALS,
        text: "OAuth 2.0 (Client Credentials)",
        type: "text" as const,
        value: SendWebhookStepAuthType.OAUTH2_CLIENT_CREDENTIALS,
      },
    ],
    [],
  );

  const selectedAuthType = useMemo(() => {
    const authType = step.authType ?? SendWebhookStepAuthType.NONE;
    return (
      authTypeOptions.find((opt) => opt.value === authType) ??
      authTypeOptions[0]
    );
  }, [authTypeOptions, step.authType]);

  const urlError = (() => {
    if (!showValidationErrors) {
      return undefined;
    }
    if (!step.destinationUrl) {
      return "Destination URL is required";
    }
    if (!isValidWebhookUrl(step.destinationUrl)) {
      return "Invalid URL format or protocol (must be http or https)";
    }
    return undefined;
  })();

  const payloadError =
    showValidationErrors && !step.payload ? "Payload is required" : undefined;

  const getHeaderKeyError = (key: string | undefined) => {
    if (!showValidationErrors) {
      return undefined;
    }
    if (!key) {
      return "Header name is required";
    }
    if (!isValidHeaderName(key)) {
      return "Invalid header name (use only letters, numbers, hyphens, and underscores)";
    }
    return undefined;
  };

  const truncatedPayload = truncateString(step.payload || "", 120);

  return (
    <Flex dir="column" gap="3xl">
      <Flex justify="space-between" py="sm">
        <Text fontSize="sm" fontWeight="semibold">
          Send webhook
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <Flex dir="column" gap="lg">
        <RedoToggle
          label="Deactivate step"
          setValue={(disabled) => setStep({ ...step, disabled })}
          size="xs"
          value={step.disabled ?? false}
        />
        <LabeledInput
          label="Destination URL"
          orientation={LabelOrientation.VERTICAL}
          position={LabelPosition.ABOVE}
          size={LabelSize.EXTRA_SMALL}
        >
          <RedoTextInput
            errorText={urlError}
            placeholder="https://example.com/webhook/{{ external_order_id }}"
            setValue={(url) => setStep({ ...step, destinationUrl: url })}
            value={step.destinationUrl || ""}
          />
        </LabeledInput>

        <LabeledInput
          label="Authentication"
          orientation={LabelOrientation.VERTICAL}
          position={LabelPosition.ABOVE}
          size={LabelSize.EXTRA_SMALL}
        >
          <RedoSingleSelectDropdownInput
            options={authTypeOptions}
            optionSelected={({ item }) => {
              setStep({
                ...step,
                authType: item.value,
                oauth2Config:
                  item.value ===
                  SendWebhookStepAuthType.OAUTH2_CLIENT_CREDENTIALS
                    ? step.oauth2Config || {
                        tokenUrl: "",
                        clientId: "",
                        clientSecret: "",
                        audience: null,
                        scope: null,
                      }
                    : step.oauth2Config,
              });
            }}
            selectedItem={selectedAuthType}
            size="xs"
          />
        </LabeledInput>

        {step.authType ===
          SendWebhookStepAuthType.OAUTH2_CLIENT_CREDENTIALS && (
          <Flex dir="column" gap="lg">
            <LabeledInput
              label="Token URL"
              orientation={LabelOrientation.VERTICAL}
              position={LabelPosition.ABOVE}
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoTextInput
                errorText={
                  showValidationErrors && !step.oauth2Config?.tokenUrl
                    ? "Token URL is required"
                    : undefined
                }
                placeholder="https://identity.example.com/oauth/token"
                setValue={(url) =>
                  setStep({
                    ...step,
                    oauth2Config: { ...step.oauth2Config!, tokenUrl: url },
                  })
                }
                size="sm"
                value={step.oauth2Config?.tokenUrl || ""}
              />
            </LabeledInput>

            <LabeledInput
              label="Client ID"
              orientation={LabelOrientation.VERTICAL}
              position={LabelPosition.ABOVE}
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoTextInput
                errorText={
                  showValidationErrors && !step.oauth2Config?.clientId
                    ? "Client ID is required"
                    : undefined
                }
                placeholder="your-client-id"
                setValue={(clientId) =>
                  setStep({
                    ...step,
                    oauth2Config: { ...step.oauth2Config!, clientId },
                  })
                }
                size="sm"
                value={step.oauth2Config?.clientId || ""}
              />
            </LabeledInput>

            <LabeledInput
              label="Client Secret"
              orientation={LabelOrientation.VERTICAL}
              position={LabelPosition.ABOVE}
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoTextInput
                errorText={
                  showValidationErrors && !step.oauth2Config?.clientSecret
                    ? "Client Secret is required"
                    : undefined
                }
                inputTypeHint="password"
                placeholder="your-client-secret"
                setValue={(clientSecret) =>
                  setStep({
                    ...step,
                    oauth2Config: { ...step.oauth2Config!, clientSecret },
                  })
                }
                size="sm"
                value={step.oauth2Config?.clientSecret || ""}
              />
            </LabeledInput>

            <LabeledInput
              label="Audience"
              orientation={LabelOrientation.VERTICAL}
              position={LabelPosition.ABOVE}
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoTextInput
                placeholder="https://api.example.com"
                setValue={(audience) =>
                  setStep({
                    ...step,
                    oauth2Config: { ...step.oauth2Config!, audience },
                  })
                }
                size="sm"
                value={step.oauth2Config?.audience || ""}
              />
            </LabeledInput>

            <LabeledInput
              label="Scope"
              orientation={LabelOrientation.VERTICAL}
              position={LabelPosition.ABOVE}
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoTextInput
                placeholder="read:data write:data"
                setValue={(scope) =>
                  setStep({
                    ...step,
                    oauth2Config: { ...step.oauth2Config!, scope },
                  })
                }
                size="sm"
                value={step.oauth2Config?.scope || ""}
              />
            </LabeledInput>
          </Flex>
        )}

        <Flex dir="column" gap="sm">
          <Flex align="center" gap="sm" justify="space-between">
            <Text fontSize="xs" fontWeight="medium">
              Headers
            </Text>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PlusIcon}
              onClick={() => {
                const newHeaders = [
                  ...(step.headers || []),
                  { key: "", value: "" },
                ];
                setStep({ ...step, headers: newHeaders });
              }}
              size="xs"
              text="Add header"
            />
          </Flex>

          {step.headers && step.headers.length > 0 && (
            <Flex dir="column" gap="2xl">
              {step.headers.map((header, index) => (
                <Flex
                  borderBottomWidth={
                    index < (step.headers?.length ?? 0) - 1 ? "1px" : undefined
                  }
                  borderColor="secondary"
                  borderStyle="solid"
                  dir="column"
                  gap="sm"
                  key={index}
                  pb={
                    index < (step.headers?.length ?? 0) - 1 ? "2xl" : undefined
                  }
                >
                  <Flex align="center" gap="sm" w="full">
                    <Flex dir="column" gap="xs" style={{ flex: 1 }}>
                      <RedoTextInput
                        errorText={getHeaderKeyError(header.key)}
                        placeholder="Header name (e.g., Authorization)"
                        setValue={(key) => {
                          const newHeaders = [...(step.headers || [])];
                          newHeaders[index] = { ...newHeaders[index], key };
                          setStep({ ...step, headers: newHeaders });
                        }}
                        size="sm"
                        value={header.key ?? ""}
                      />
                    </Flex>
                    <RedoButton
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={() => {
                        const newHeaders = [...(step.headers || [])];
                        newHeaders.splice(index, 1);
                        setStep({ ...step, headers: newHeaders });
                      }}
                      size="sm"
                    />
                  </Flex>
                  <RedoTextInput
                    placeholder="Header value (e.g., Bearer <token>)"
                    setValue={(value) => {
                      const newHeaders = [...(step.headers || [])];
                      newHeaders[index] = { ...newHeaders[index], value };
                      setStep({ ...step, headers: newHeaders });
                    }}
                    size="sm"
                    value={header.value ?? ""}
                  />
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex dir="column" gap="xs">
          <Flex align="center" gap="sm" justify="space-between">
            <Text fontSize="xs" fontWeight="medium">
              Payload (JSON)
            </Text>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PenIcon}
              onClick={() => setPayloadEditorOpen(true)}
              size="xs"
              text="Edit payload"
            />
          </Flex>
          <RedoTextareaInputField
            disabled
            errorText={payloadError}
            hintText="Click 'Edit payload' to configure the webhook payload"
            monospace
            placeholder="{}"
            setValue={() => {}}
            value={truncatedPayload}
          />
        </Flex>
      </Flex>
      <PayloadEditorModal
        availableDynamicVariables={availableDynamicVariables}
        isOpen={payloadEditorOpen}
        onClose={() => setPayloadEditorOpen(false)}
        payload={step.payload || ""}
        schemaType={schemaType}
        setPayload={(payload) => setStep({ ...step, payload })}
      />
    </Flex>
  );
});

export const validateSendWebhookStep = (
  step: PartialWithRequiredType<SendWebhookStep>,
) => {
  const basicValidation =
    !!step.payload &&
    isValidWebhookUrl(step.destinationUrl) &&
    (step.headers?.every(
      (header) => header.key && isValidHeaderName(header.key),
    ) ??
      true);

  if (!basicValidation) {
    return false;
  }

  if (step.authType === SendWebhookStepAuthType.OAUTH2_CLIENT_CREDENTIALS) {
    return !!(
      step.oauth2Config?.tokenUrl &&
      step.oauth2Config?.clientId &&
      step.oauth2Config?.clientSecret
    );
  }

  return true;
};

export const sendWebhookStepType: FrontendStepType<
  PartialWithRequiredType<SendWebhookStep>
> = {
  valid: validateSendWebhookStep,
  type: "custom",
  customInspector: WebhookStepInspector,
};
